import { DefaultError, useMutation } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { useNavigate } from 'react-router-dom';
import { useActions } from 'store/state';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import { warningToast } from 'utils/toast/warningToast';
import { IAuthResponse, LoginMutation, RefreshTokenMutation } from './types';
import { useStoreState } from 'providers/StoreStateContext';

export const useLogin = () => {
  const { responseHandler } = useResponseHandler();
  const { setToken, setRefreshToken, setTwoFactorTokenExpiresAt, setServiceTokenExpiresAt } =
    useActions();
  const navigate = useNavigate();
  const { loading, success, dismiss } = useQueryToast('useLogin');
  return useMutation<IAuthResponse, DefaultError, LoginMutation>({
    async mutationFn(body) {
      loading(i18next.t('service-message.auth.login.loading'));
      const response = await apiClient({
        method: 'POST',
        url: 'Auth/Login',
        body,
      });

      return responseHandler(response, {
        groupName: 'useLogin',
      });
    },
    async onSuccess(data, variables) {
      if (data.require2FA) {
        const urlWithParams = `/two-factor/${data.token2FA}/${variables.email}`;
        setTwoFactorTokenExpiresAt(data.token2FAExpires);
        navigate(urlWithParams);
        dismiss();
        return;
      }
      if (data.requireServiceCode) {
        const urlWithParams = `/service-code/${data.tokenServiceCode}`;
        setServiceTokenExpiresAt(data.tokenServiceCodeExpires);
        navigate(urlWithParams);
        dismiss();
        return;
      }
      if (data.isPasswordChangeRequired) {
        warningToast('toast-message.force-change-password');
      }
      success(i18next.t('service-message.auth.login.success'));
      setToken(data.token);
      setRefreshToken(data.refreshToken);
    },
    onError: dismiss,
  });
};

export const useFetchRefreshToken = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast('useFetchRefreshToken');
  const { clearTokens } = useStoreState();
  const navigate = useNavigate();
  return useMutation<IAuthResponse, DefaultError, RefreshTokenMutation>({
    async mutationFn(body) {
      loading(i18next.t('service-message.auth.refresh-token.loading'));

      const response = await apiClient({
        method: 'POST',
        url: 'Auth/RefreshToken',
        body,
      });

      return responseHandler(response, {
        groupName: 'useFetchRefreshToken',
      });
    },
    onSuccess(data) {
      if (data.isPasswordChangeRequired) {
        warningToast('toast-message.force-change-password');
      }
      success(i18next.t('service-message.auth.refresh-token.success'));
    },
    onError(error) {
      clearTokens();
      navigate('/login', { replace: true });
      dismiss();
    },
  });
};
